<template>
  <v-card variant="outlined" class="blog-comment mb-3" :class="{ 'ml-10': isReply }">
    <v-card-text class="py-3">
      <!-- Author line -->
      <div class="d-flex align-center ga-2 mb-2">
        <v-avatar size="28">
          <v-img v-if="comment.avatarUrl" :src="comment.avatarUrl" />
          <v-icon v-else size="28">mdi-account-circle</v-icon>
        </v-avatar>
        <router-link
          :to="{ name: 'profile', params: { username: comment.username } }"
          class="font-weight-medium text-body-2 text-decoration-none"
        >
          {{ comment.displayName || comment.username }}
        </router-link>
        <span class="text-caption text-grey">{{ timeAgo(comment.created_at) }}</span>
        <v-chip v-if="comment.isEdited" size="x-small" variant="text" class="text-grey">
          (redigeret)
        </v-chip>
      </div>

      <!-- Quoted text -->
      <div v-if="comment.quotedText" class="quoted-text mb-2">
        <v-icon size="14" class="mr-1">mdi-format-quote-open</v-icon>
        {{ comment.quotedText }}
      </div>

      <!-- Comment body -->
      <div v-if="!isEditing" class="text-body-2 comment-body" v-html="renderComment(comment.body)" />

      <!-- Edit form -->
      <v-textarea
        v-else
        v-model="editText"
        variant="outlined"
        density="compact"
        rows="2"
        auto-grow
        hide-details
        class="mb-2"
      />

      <!-- Actions -->
      <div class="d-flex align-center ga-1 mt-2">
        <!-- Reactions -->
        <v-btn
          size="x-small"
          variant="text"
          :color="comment.userReaction ? 'primary' : 'grey'"
          @click="toggleReaction('like')"
        >
          <v-icon start size="14">{{ comment.userReaction ? 'mdi-heart' : 'mdi-heart-outline' }}</v-icon>
          {{ comment.reactionCount || '' }}
        </v-btn>

        <!-- Reply (only on top-level) -->
        <v-btn v-if="!isReply" size="x-small" variant="text" @click="showReplyForm = !showReplyForm">
          <v-icon start size="14">mdi-reply</v-icon>
          {{ $t('blog.reply') }}
        </v-btn>

        <!-- Quote reply -->
        <v-btn size="x-small" variant="text" @click="quoteReply">
          <v-icon start size="14">mdi-format-quote-close</v-icon>
          {{ $t('blog.quote') }}
        </v-btn>

        <v-spacer />

        <!-- Edit/Delete own comment -->
        <template v-if="isOwnComment">
          <v-btn v-if="!isEditing" size="x-small" variant="text" @click="startEdit">
            <v-icon size="14">mdi-pencil</v-icon>
          </v-btn>
          <template v-else>
            <v-btn size="x-small" variant="text" color="primary" @click="saveEdit">
              <v-icon size="14">mdi-check</v-icon>
            </v-btn>
            <v-btn size="x-small" variant="text" @click="cancelEdit">
              <v-icon size="14">mdi-close</v-icon>
            </v-btn>
          </template>
          <v-btn size="x-small" variant="text" color="error" @click="deleteComment">
            <v-icon size="14">mdi-delete</v-icon>
          </v-btn>
        </template>

        <!-- Flag -->
        <v-btn v-if="!isOwnComment" size="x-small" variant="text" @click="showFlagDialog = true">
          <v-icon size="14">mdi-flag-outline</v-icon>
        </v-btn>
      </div>

      <!-- Reply form -->
      <blog-comment-form
        v-if="showReplyForm"
        :post-id="postId"
        :parent-id="comment.commentId"
        class="mt-3"
        compact
        @submitted="onReplySubmitted"
      />
    </v-card-text>

    <!-- Replies (collapsible) -->
    <template v-if="!isReply && comment.replies && comment.replies.length > 0">
      <v-btn
        variant="text"
        size="small"
        class="ml-4 mb-2"
        @click="repliesExpanded = !repliesExpanded"
      >
        <v-icon start size="16">{{ repliesExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        {{ repliesExpanded
          ? $t('blog.hideReplies')
          : $t('blog.showReplies', { count: comment.replies.length })
        }}
      </v-btn>

      <v-expand-transition>
        <div v-show="repliesExpanded" class="px-3 pb-3">
          <blog-comment
            v-for="reply in comment.replies"
            :key="reply.commentId"
            :comment="reply"
            :post-id="postId"
            is-reply
            @reply="$emit('reply')"
            @deleted="$emit('deleted')"
          />
        </div>
      </v-expand-transition>
    </template>

    <!-- Flag dialog -->
    <v-dialog v-model="showFlagDialog" max-width="420">
      <v-card>
        <v-card-title>{{ $t('blog.reportComment') }}</v-card-title>
        <v-card-text>
          <v-radio-group v-model="flagReason">
            <v-radio label="Spam" value="spam" />
            <v-radio label="Stødende indhold" value="offensive" />
            <v-radio label="Misinformation" value="misinformation" />
            <v-radio label="Chikane" value="harassment" />
            <v-radio label="Andet" value="other" />
          </v-radio-group>
          <v-textarea
            v-if="flagReason === 'other'"
            v-model="flagDetails"
            :label="$t('blog.flagDetails')"
            variant="outlined"
            rows="2"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showFlagDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" @click="submitFlag">{{ $t('blog.report') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
/* eslint-disable */
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import BlogCommentForm from './BlogCommentForm.vue'

const props = defineProps({
  comment: { type: Object, required: true },
  postId: { type: Number, required: true },
  isReply: { type: Boolean, default: false }
})

const emit = defineEmits(['reply', 'deleted'])

const store = useStore()

const currentUserId = computed(() => store.getters['auth/userId'])
const isOwnComment = computed(() => props.comment.userId === currentUserId.value)

const showReplyForm = ref(false)
const repliesExpanded = ref(true)
const isEditing = ref(false)
const editText = ref('')
const showFlagDialog = ref(false)
const flagReason = ref('spam')
const flagDetails = ref('')

const timeAgo = (date) => {
  const now = new Date()
  const then = new Date(date)
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'lige nu'
  if (diffMin < 60) return `${diffMin} min siden`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours}t siden`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 30) return `${diffDays}d siden`
  return then.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
}

const renderComment = (body) => {
  if (!body) return ''
  return body
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>')
    .replace(/@(\w+)/g, '<a href="/profile/$1" class="mention">@$1</a>')
}

const toggleReaction = async (type) => {
  await store.dispatch('blog/toggleReaction', {
    postId: props.postId,
    reactionType: type,
    commentId: props.comment.commentId
  })
  emit('reply') // triggers refresh
}

const quoteReply = () => {
  const text = props.comment.body.substring(0, 200)
  showReplyForm.value = true
  // The form will pick up quotedText via props if we emit
}

const startEdit = () => {
  editText.value = props.comment.body
  isEditing.value = true
}

const cancelEdit = () => {
  isEditing.value = false
  editText.value = ''
}

const saveEdit = async () => {
  if (!editText.value.trim()) return
  await store.dispatch('blog/editComment', {
    postId: props.postId,
    commentId: props.comment.commentId,
    body: editText.value.trim()
  })
  isEditing.value = false
  emit('reply') // refresh
}

const deleteComment = async () => {
  if (!confirm('Slet denne kommentar?')) return
  await store.dispatch('blog/deleteComment', {
    postId: props.postId,
    commentId: props.comment.commentId
  })
  emit('deleted')
}

const onReplySubmitted = () => {
  showReplyForm.value = false
  emit('reply')
}

const submitFlag = async () => {
  await store.dispatch('blog/flagContent', {
    commentId: props.comment.commentId,
    reason: flagReason.value,
    details: flagDetails.value || null
  })
  showFlagDialog.value = false
}
</script>

<style scoped>
.quoted-text {
  border-left: 3px solid rgba(var(--v-theme-primary), 0.4);
  padding: 4px 12px;
  background: rgba(var(--v-theme-primary), 0.04);
  border-radius: 0 4px 4px 0;
  font-style: italic;
  font-size: 0.85rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}
.comment-body :deep(.mention) {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  font-weight: 500;
}
</style>
